import React from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './TopBar';
import PageBanner from './PageBanner';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import ResumeInlineBanner from '../appNotice/ResumeInlineBanner';

/**
 * Handoff page shell: TopBar → PageBanner → optional full-bleed strip → main.
 * Content container is 1440px max with 32px horizontal padding (16px mobile),
 * 32px top / 72px bottom rhythm.
 *
 * The shell's root paints nothing: the banner band shows the page's LASERS layer
 * (chrome/LasersBackground, mounted once by AppBackground) through it, and only the
 * content below the banner sits on the #F5F5F5 canvas — the same look pages had,
 * minus the retired skybox wash on the band.
 */
export default function PageShell({
  title,
  subtitle,
  eyebrow,
  active,
  showNav = true,
  bannerActions,
  titleClassName = '',
  strip,
  children,
  contentClassName = '',
  bare = false,
}) {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // overflow-x-clip (not -hidden) contains a stray over-wide child without
  // creating a scroll container, so the sticky top bar keeps working.
  return (
    <div className="flex min-h-screen flex-col overflow-x-clip">
      <TopBar
        user={profile || user}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/featured')}
      />

      {title && (
        <PageBanner
          title={title}
          subtitle={subtitle}
          eyebrow={eyebrow}
          active={active}
          showNav={showNav}
          actions={bannerActions}
          titleClassName={titleClassName}
        />
      )}

      <div className="flex flex-1 flex-col bg-hand-canvas">
      {/* The downgraded resume prompt: after three dismissals it stops
          interrupting and asks from here instead. Renders nothing otherwise. */}
      <ResumeInlineBanner />

      {strip}

      {bare ? children : (
        <main className={`mx-auto w-full max-w-content px-4 pb-[72px] pt-8 sm:px-8 ${contentClassName}`}>
          {children}
        </main>
      )}
      </div>
    </div>
  );
}
